import { streamingRemoteFeedback } from "../i18n/streamingFeedbackPresentation";
import { type StreamingMessage, useStreamingLanguage, streamMessage, streamLocal, streamText } from "../i18n/streamingDashboardPresentation";
/**
 * useWSDSessions — React hook for WSD (What's Streaming Dashboard) streaming sessions.
 *
 * Connects to GET /api/streaming/stream via SSE for real-time session updates.
 * Also subscribes to the media-server event stream for fast-path session.end removal.
 * Includes exponential backoff reconnect and session action methods.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { runSessionAction } from "../lib/streaming/sessionAction";
import {
  sortSessionsStable,
  createFirstSeenGetter,
} from "../lib/streaming/sessionSort";
import {
  useMediaServerEventStream,
  useMediaServerEventStreamStatus,
  type MediaServerStreamStatus,
} from "./useMediaServerEventStream.js";

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirrored from backend src/domain/streaming/types.ts)
// ─────────────────────────────────────────────────────────────────────────────

export type MediaServerType = "emby" | "jellyfin" | "plex";
export type MediaType = "Movie" | "Episode" | "Live TV" | "Audio" | "Unknown";

export interface MediaDetails {
  titleSource?: unknown;
  title: string;
  type: MediaType;
  year?: number | null;
  duration?: number | null;
  pausePosition?: string;
  season?: number | string;
  episode?: number | string;
  episodeTitle?: string;
  /** Live TV channel call sign (e.g. "HGTV"). */
  channelName?: string;
  /** Live TV channel number (e.g. "1622" — Dispatcharr/Emby numeric channel). */
  channelNumber?: string;
  nowPlaying?: string;
  /** Current-programme airing window (epoch ms), from a guide override. */
  programStartMs?: number;
  programEndMs?: number;
  user?: string;
  device?: string;
  itemId?: string;
  seriesId?: string;
}

/**
 * Guide-resolved Live TV identity, attached only to `mediaDetails.type === "Live
 * TV"` sessions. `lineupKey` is the guide grid row key (`<layout stem>:<chno>`)
 * when the session's channel maps to a guide row, else null; `provider` is the
 * resolved IPTVBoss source, else null (a tuner `sourceLabel` fallback applies).
 */
export interface LiveTvSessionInfo {
  lineupKey: string | null;
  channelNumber: string | null;
  channelName: string | null;
  provider: { id: string; name: string } | null;
  /** The channel's logo (signed path or `/uploads`), already proxied; null when none. */
  logoUrl: string | null;
}

export interface GeoInfo {
  ipAddress: string;
  countryCode?: string | null;
  countryName?: string | null;
  city?: string | null;
  region?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  isp?: string | null;
  isPrivate: boolean;
  // Privacy/threat detection
  isVpn?: boolean;
  isProxy?: boolean;
  isTor?: boolean;
  isHosting?: boolean;
}

export interface DeviceInfo {
  deviceId: string;
  deviceName: string;
  customName?: string | null;
  platform?: string | null;
  clientVersion?: string | null;
  userAgent?: string | null;
  ipAddress?: string | null;
  appIconUrl?: string | null;
  isActive: boolean;
  firstSeen: number;
  lastSeen: number;
  connectionCount: number;
}

export interface PlaybackProgress {
  positionTicks: number;
  durationTicks: number;
  percentComplete: number;
  formattedPosition: string;
  formattedDuration: string;
  formattedRemaining: string;
}

export interface TranscodingStatus {
  isTranscoding: boolean;
  videoCodec?: string;
  audioCodec?: string;
  container?: string;
  bitrate?: number;
  width?: number;
  height?: number;
  completionPercentage?: number;
  hardwareAcceleration?: string;
  reasons?: string[];
  framerate?: number;
  audioChannels?: number;
}

export interface OriginalMediaInfo {
  container?: string;
  bitrate?: number;
  videoCodec?: string;
  videoWidth?: number;
  videoHeight?: number;
  videoBitrate?: number;
  audioCodec?: string;
  audioChannels?: number;
  audioChannelLayout?: string;
  audioBitrate?: number;
  audioLanguage?: string;
  subtitleLanguage?: string;
  subtitleTitle?: string;
  subtitleIsExternal?: boolean;
}

export interface WSDSession {
  id: string;
  serverId?: string;
  serverType: MediaServerType;
  userId: string;
  userName: string;
  device: DeviceInfo;
  isPaused: boolean;
  isPlaying: boolean;
  playMethod?: "DirectPlay" | "DirectStream" | "Transcode";
  mediaDetails: MediaDetails | null;
  progress: PlaybackProgress | null;
  transcoding: TranscodingStatus | null;
  originalMedia: OriginalMediaInfo | null;
  thumbnailUrl: string | null;
  tmdbId?: string | null;
  tvdbId?: string | null;
  geo: GeoInfo | null;
  lastActivityAt: number;
  pausedAt?: number | null;
  supportsRemoteControl: boolean;
  supportsMediaControl: boolean;
  sourceLabel?: string | null;
  sourceLabelPresentation?: unknown;
  sourceColor?: string | null;
  mediaSourceId?: string | null;
  canonicalState?: "playing" | "paused" | "buffering" | "stop_requested" | "stop_confirmed" | "terminated" | "error" | "unknown";

  /** Stable compound key: serverType:userId:deviceId:mediaItemId */
  compoundKey?: string;
  /** Current continuous pause duration in seconds (resets on resume) */
  currentPauseDuration?: number;
  /** Total accumulated pause duration across all pause/resume cycles (seconds) */
  totalPauseDuration?: number;
  /** Number of pause events in this session */
  pauseCount?: number;
  /** True when the real client IP is unavailable (e.g. VPN-routed Plex sessions); IP and geo are nulled. */
  clientIpUnavailable?: boolean;
  /** Guide-resolved Live TV identity, present only on Live TV sessions. */
  liveTv?: LiveTvSessionInfo;
}

export interface SessionStats {
  totalSessions: number;
  activeSessions: number;
  pausedSessions: number;
  transcodingSessions: number;
  directPlaySessions: number;
  directStreamSessions: number;
  byUser: Record<string, number>;
  byDevice: Record<string, number>;
  byMediaType: Record<MediaType, number>;
}

export interface SessionActionResult {
  success: boolean;
  sessionId: string;
  action: string;
  message?: string;
  error?: string;
}

/** Per-server health status from backend stale-while-revalidate gap protection */
export type ServerHealthState = "healthy" | "degraded" | "unreachable";

export interface ServerHealthStatus {
  state: ServerHealthState;
  consecutiveFailures: number;
  lastSuccessAt: number | null;
  stale: boolean;
}

export type ServerHealthMap = Partial<Record<MediaServerType, ServerHealthStatus>>;

type StreamStatus = "connecting" | "connected" | "reconnecting" | "error" | "disabled";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_RECONNECT_DELAY_MS = 15000;

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export const useWSDSessions = () => {
  const [status, setStatus] = useState<StreamStatus>("connecting");
  const [sessions, setSessions] = useState<WSDSession[]>([]);
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [serverHealth, setServerHealth] = useState<ServerHealthMap | null>(null);
  const [lastPolicyUpdate, setLastPolicyUpdate] = useState<{
    serverType: string;
    userId: string;
    source: string;
    timestamp: number;
  } | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const connectRef = useRef<() => void>(() => undefined);

  // Track when each session was first seen (stable join timestamp for sorting)
  // This is frontend-only tracking since the API doesn't provide session start time
  const firstSeenMapRef = useRef<Map<string, number>>(new Map());

  // ─────────────────────────────────────────────────────────────────────────────
  // SSE Connection Management
  // ─────────────────────────────────────────────────────────────────────────────

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const disconnect = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  const scheduleReconnect = useCallback((delayMs: number) => {
    clearReconnectTimer();
    setStatus("reconnecting");
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      retryCountRef.current += 1;
      connectRef.current();
    }, delayMs);
  }, []);

  const connect = useCallback(() => {
    clearReconnectTimer();
    disconnect();
    setStatus("connecting");
    setError(null);

    if (typeof EventSource === "undefined") {
      setStatus("error");
      setError(streamLocal("eventSourceNotSupported"));
      return;
    }

    const source = new EventSource("/api/streaming/stream");
    eventSourceRef.current = source;

    source.addEventListener("hello", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        if (!data.ok) {
          // WSD not enabled or not configured
          setStatus("disabled");
          setError((streamingRemoteFeedback(data) ?? data.message ?? streamLocal("wSDNotEnabled")));
          setEnabled(false);
          disconnect();
          return;
        }
        setStatus("connected");
        setEnabled(true);
        retryCountRef.current = 0;
      } catch {
        // Invalid payload, continue anyway
        setStatus("connected");
        retryCountRef.current = 0;
      }
    });

    source.addEventListener("sessions", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        if (Array.isArray(data.sessions)) {
          const incomingSessions = data.sessions as WSDSession[];
          const now = Date.now();
          const currentIds = new Set<string>();

          // Track firstSeenAt for each session
          for (const session of incomingSessions) {
            currentIds.add(session.id);
            // Only set firstSeenAt if this is a new session
            if (!firstSeenMapRef.current.has(session.id)) {
              firstSeenMapRef.current.set(session.id, now);
            }
          }

          // Clean up sessions that are no longer present
          for (const id of firstSeenMapRef.current.keys()) {
            if (!currentIds.has(id)) {
              firstSeenMapRef.current.delete(id);
            }
          }

          setSessions(incomingSessions);
        }
        if (data.stats) {
          setStats(data.stats);
        }
        // Parse server health for stale-while-revalidate indicator
        setServerHealth(data.serverHealth ?? null);

        setStatus("connected");
        setEnabled(true);
      } catch {
        // Invalid payload, skip
      }
    });

    source.addEventListener("session:update", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as WSDSession;

        // Track firstSeenAt if this is a new session
        if (!firstSeenMapRef.current.has(data.id)) {
          firstSeenMapRef.current.set(data.id, Date.now());
        }

        setSessions((prev) => {
          const index = prev.findIndex((s) => s.id === data.id);
          if (index >= 0) {
            const next = [...prev];
            next[index] = data;
            return next;
          }
          return [...prev, data];
        });
      } catch {
        // Invalid payload, skip
      }
    });

    source.addEventListener("session:remove", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        const sessionId = data.sessionId ?? data.id;
        if (sessionId) {
          // Clean up firstSeenAt tracking
          firstSeenMapRef.current.delete(sessionId);
          setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        }
      } catch {
        // Invalid payload, skip
      }
    });

    source.addEventListener("stats", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as SessionStats;
        setStats(data);
      } catch {
        // Invalid payload, skip
      }
    });

    source.addEventListener("user:policy:updated", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as {
          serverType: string;
          userId: string;
          source: string;
        };
        setLastPolicyUpdate({ ...data, timestamp: Date.now() });
      } catch {
        // Invalid payload, skip
      }
    });

    source.addEventListener("error", () => {
      const attempt = retryCountRef.current;
      const delay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** attempt);
      setStatus("error");
      setError(streamLocal("connectionLost"));
      disconnect();
      scheduleReconnect(delay);
    });
  }, [scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const reconnect = useCallback(() => {
    retryCountRef.current = 0;
    connect();
  }, [connect]);

  useEffect(() => {
    connect();
    return () => {
      clearReconnectTimer();
      disconnect();
    };
  }, [connect]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Session Actions
  // ─────────────────────────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    (
      sessionId: string,
      header: string | undefined,
      text: string,
      serverType?: MediaServerType
    ): Promise<SessionActionResult> => {
      const body: { text: string; header?: string; serverType?: MediaServerType } = { text };
      if (header) body.header = header;
      if (serverType) body.serverType = serverType;
      return runSessionAction({
        sessionId,
        action: "message",
        body,
        successText: streamText("messageSent"),
        failureText: streamText("failedToSendMessage"),
      });
    },
    []
  );

  const stopPlayback = useCallback(
    (sessionId: string, serverType?: MediaServerType): Promise<SessionActionResult> =>
      runSessionAction({
        sessionId,
        action: "stop",
        body: { serverType },
        successText: streamText("playbackStopped"),
        failureText: streamText("failedToStopPlayback"),
      }),
    []
  );

  const killSession = useCallback(
    (sessionId: string, serverType?: MediaServerType): Promise<SessionActionResult> =>
      runSessionAction({
        sessionId,
        action: "kill",
        body: { serverType },
        successText: streamText("sessionTerminated"),
        failureText: streamText("failedToKillSession"),
      }),
    []
  );

  const pausePlayback = useCallback(
    (sessionId: string, serverType?: MediaServerType): Promise<SessionActionResult> =>
      runSessionAction({
        sessionId,
        action: "pause",
        body: { serverType },
        successText: streamText("playbackPaused"),
        failureText: streamText("failedToPausePlayback"),
      }),
    []
  );

  const resumePlayback = useCallback(
    (sessionId: string, serverType?: MediaServerType): Promise<SessionActionResult> =>
      runSessionAction({
        sessionId,
        action: "resume",
        body: { serverType },
        successText: streamText("playbackResumed"),
        failureText: streamText("failedToResumePlayback"),
      }),
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Derived State
  // ─────────────────────────────────────────────────────────────────────────────

  /** Sessions that are currently paused */
  const pausedSessions = useMemo(
    () => sessions.filter((s) => s.isPaused && s.pausedAt != null),
    [sessions]
  );

  /**
   * Compute stats from sessions array.
   * This ensures stats always reflect the current session state,
   * updating immediately when sessions change (e.g., pause/resume).
   * Falls back to server-provided stats if available and sessions empty.
   */
  const computedStats = useMemo((): SessionStats | null => {
    // Always compute from live sessions so stats react instantly to session.end
    // and session:update events. The server-provided stats snapshot is stale
    // relative to the fast-path event stream and must not override live state.
    const activeSessions = sessions.filter((s) => s.isPlaying && !s.isPaused).length;
    const pausedCount = sessions.filter((s) => s.isPaused).length;
    const transcodingSessions = sessions.filter((s) => s.playMethod === "Transcode").length;
    const directPlaySessions = sessions.filter((s) => s.playMethod === "DirectPlay").length;
    const directStreamSessions = sessions.filter((s) => s.playMethod === "DirectStream").length;

    const byUser: Record<string, number> = {};
    const byDevice: Record<string, number> = {};
    const byMediaType: Record<MediaType, number> = {
      Movie: 0,
      Episode: 0,
      "Live TV": 0,
      Audio: 0,
      Unknown: 0,
    };

    for (const session of sessions) {
      // By user
      byUser[session.userName] = (byUser[session.userName] ?? 0) + 1;

      // By device
      const deviceName = session.device.deviceName || "Unknown";
      byDevice[deviceName] = (byDevice[deviceName] ?? 0) + 1;

      // By media type
      const mediaType = session.mediaDetails?.type ?? "Unknown";
      byMediaType[mediaType] = (byMediaType[mediaType] ?? 0) + 1;
    }

    return {
      totalSessions: sessions.length,
      activeSessions,
      pausedSessions: pausedCount,
      transcodingSessions,
      directPlaySessions,
      directStreamSessions,
      byUser,
      byDevice,
      byMediaType,
    };
  }, [sessions]);

  /** Get pause duration in seconds for a session */
  const getPauseDuration = useCallback(
    (sessionId: string): number | null => {
      const session = sessions.find((s) => s.id === sessionId);
      if (!session?.pausedAt) return null;
      return Math.floor(Date.now() / 1000) - session.pausedAt;
    },
    [sessions]
  );

  /** Get firstSeenAt timestamp for a session (for sorting) */
  const getFirstSeenAt = useCallback(
    (sessionId: string): number => {
      return firstSeenMapRef.current.get(sessionId) ?? Date.now();
    },
    []
  );

  /**
   * Sessions sorted with stable ordering:
   * - Paused sessions always last
   * - Newest-joined sessions first (by firstSeenAt DESC)
   * - Deterministic tie-breakers (userName, sessionId)
   */
  const sortedSessions = useMemo(
    () => sortSessionsStable(sessions, createFirstSeenGetter(firstSeenMapRef.current)),
    [sessions]
  );

  /**
   * Whether any configured server is in a degraded or unreachable state.
   * Used by the UI to show a subtle "stale data" indicator.
   */
  const isServerDegraded = useMemo((): boolean => {
    if (!serverHealth) return false;
    return Object.values(serverHealth).some(
      (h) => h && (h.state === "degraded" || h.state === "unreachable")
    );
  }, [serverHealth]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Media server event stream — fast-path session updates
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * session.end — immediately remove the session from local state.
   * The primary WSD SSE stream will confirm on next snapshot,
   * but this eliminates the ghost-session display delay.
   */
  useMediaServerEventStream("session.end", useCallback((event) => {
    const sessionId = event.sessionId;
    if (!sessionId) return;
    firstSeenMapRef.current.delete(sessionId);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []));

  /** Status of the media server event stream relay (for UI indicators) */
  const eventStreamStatus: MediaServerStreamStatus = useMediaServerEventStreamStatus();

  // ─────────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────────

  return {
    status,
    sessions,
    /** Sessions sorted with stable ordering (paused last, newest first) */
    sortedSessions,
    pausedSessions,
    stats: computedStats,
    error,
    enabled,
    reconnect,
    sendMessage,
    stopPlayback,
    killSession,
    pausePlayback,
    resumePlayback,
    getPauseDuration,
    /** Get firstSeenAt timestamp for a session (for sorting) */
    getFirstSeenAt,
    /** Per-server health map (null when all servers healthy) */
    serverHealth,
    /** True when any server is degraded or unreachable (stale data being shown) */
    isServerDegraded,
    /** Last user:policy:updated SSE event (null if none received) */
    lastPolicyUpdate,
    /**
     * Status of the media server event relay (separate from the WSD session stream).
     * 'open' = event relay connected; 'connecting' = reconnecting; 'closed' = offline.
     */
    eventStreamStatus,
  };
};
